import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  Space,
  Button,
  Descriptions,
  Typography,
  Spin,
  Tag,
  Table,
  Empty,
  message,
} from "antd";
import { SafetyCertificateOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  LeanCustomsService,
  LeanCustomsDeclaration,
  LeanCustomsDeclarationDocument,
  LeanCustomsInvoice,
} from "../services/leancustomsService";
import { useCompany } from "../hooks/useCompany";
import {
  DECLARATION_STATUS_COLORS,
  DECLARATION_STATUS_LABELS,
} from "../types/leancustoms";
import {
  getDeclarationDocumentOfficialKind,
  sortDeclarationDocumentsUtbFirst,
} from "../utils/leanCustomsDeclarationDocumentsSort";
import { extractUtbTtwAmountDisplays } from "../utils/leanCustomsUtbTtwAmounts";
import { formatLeanCustomsOrderDetailLine } from "../utils/leanCustomsOrderDisplay";
import {
  pickDeclarationsForBooking,
  pickDeclarationsForLinkedOrder,
} from "../utils/leanCustomsDeclarationLookup";
import { searchMatchingLeanCustomsDeclarations } from "../services/leanCustomsBookingSearch";
import { pickString, formatByteSize } from "../utils/leanCustomsRecordUtils";
import { CUSTOMS_PAGE_ENABLED } from "../constants/leancustoms";

export interface BookingTrackerLeanCustomsSectionProps {
  bookingId: number;
  bookingReference: string;
  organizationId: string;
  bookingCompanyId: number;
  orderId?: string | null;
  declarationId?: string | null;
}

/**
 * Booking view: Lean Customs order and/or declaration (lookup by EFF reference when not linked).
 */
export const BookingTrackerLeanCustomsSection: React.FC<
  BookingTrackerLeanCustomsSectionProps
> = ({
  bookingId,
  bookingReference,
  organizationId,
  bookingCompanyId,
  orderId,
  declarationId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyDetails } = useCompany();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [secondaryWarning, setSecondaryWarning] = useState<string | null>(null);
  const [declaration, setDeclaration] = useState<LeanCustomsDeclaration | null>(
    null
  );
  const [order, setOrder] = useState<Record<string, unknown> | null>(null);
  const [documents, setDocuments] = useState<LeanCustomsDeclarationDocument[]>(
    []
  );
  const [invoices, setInvoices] = useState<LeanCustomsInvoice[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [resolvedDeclarationId, setResolvedDeclarationId] = useState<
    string | null
  >(null);
  const [visible, setVisible] = useState(true);

  const linkedOrderId = orderId?.trim() || null;

  const showPortalLink =
    CUSTOMS_PAGE_ENABLED &&
    Boolean(resolvedDeclarationId) &&
    Boolean(companyDetails?.leancustoms_organization_id) &&
    Boolean(companyDetails?.leancustoms_contract_id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setSecondaryWarning(null);
      setVisible(true);

      try {
        let declId = declarationId?.trim() || null;
        const warnings: string[] = [];

        if (!declId) {
          const candidates = await searchMatchingLeanCustomsDeclarations(
            organizationId,
            bookingCompanyId,
            bookingId,
            bookingReference,
            null,
            linkedOrderId
          );
          if (cancelled) return;

          const picked = linkedOrderId
            ? pickDeclarationsForLinkedOrder(candidates, linkedOrderId, bookingId)
            : pickDeclarationsForBooking(candidates, bookingId);
          declId = picked[0]?.id ?? null;
        } else {
          const getRes = await LeanCustomsService.getDeclaration(
            declId,
            bookingCompanyId
          );
          if (!getRes.success && getRes.error) {
            warnings.push(getRes.error);
          }
        }

        setResolvedDeclarationId(declId);

        if (linkedOrderId) {
          const ordRes = await LeanCustomsService.getOrder(
            linkedOrderId,
            bookingCompanyId
          );
          if (cancelled) return;

          if (ordRes.success && ordRes.data) {
            setOrder(ordRes.data);
            setLoadError(null);
          } else {
            setOrder(null);
            if (!declId) {
              setDeclaration(null);
              setDocuments([]);
              setInvoices([]);
              setLoadError(ordRes.error || t("bookings.leanCustoms.loadError"));
              setSecondaryWarning(null);
              return;
            }
            if (ordRes.error) warnings.push(ordRes.error);
          }
        } else {
          setOrder(null);
          if (!declId) {
            setVisible(false);
            return;
          }
        }

        if (!declId) {
          setDeclaration(null);
          setDocuments([]);
          setInvoices([]);
          setSecondaryWarning(warnings.length ? warnings.join(" · ") : null);
          return;
        }

        const [decRes, docRes, invRes] = await Promise.all([
          LeanCustomsService.getDeclaration(declId, bookingCompanyId),
          LeanCustomsService.listDeclarationDocuments(declId, bookingCompanyId),
          LeanCustomsService.listDeclarationInvoices(declId, bookingCompanyId),
        ]);

        if (cancelled) return;

        if (!decRes.success) {
          setDeclaration(null);
          if (decRes.error) warnings.push(decRes.error);
        } else {
          setDeclaration(decRes.data ?? null);
        }

        if (docRes.success && docRes.data?.results) {
          setDocuments(sortDeclarationDocumentsUtbFirst(docRes.data.results));
        } else {
          setDocuments([]);
          if (docRes.error) warnings.push(docRes.error);
        }

        if (invRes.success && invRes.data?.results) {
          setInvoices(invRes.data.results);
        } else {
          setInvoices([]);
          if (invRes.error) warnings.push(invRes.error);
        }

        setLoadError(null);
        setSecondaryWarning(warnings.length ? warnings.join(" · ") : null);
      } catch {
        if (!cancelled) {
          setLoadError(t("bookings.leanCustoms.loadError"));
          setSecondaryWarning(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    linkedOrderId,
    declarationId,
    bookingId,
    bookingReference,
    organizationId,
    bookingCompanyId,
    t,
  ]);

  const handleDownload = useCallback(
    async (docId: string, filename: string) => {
      if (!resolvedDeclarationId) return;
      setDownloadingId(docId);
      try {
        const blob = await LeanCustomsService.downloadDeclarationDocument(
          resolvedDeclarationId,
          docId,
          bookingCompanyId
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success(t("customs.documentDownloaded"));
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : t("customs.errors.failedToDownloadDocument");
        message.error(msg);
      } finally {
        setDownloadingId(null);
      }
    },
    [resolvedDeclarationId, bookingCompanyId, t]
  );

  const dmsStatus = declaration?.dms_status || "unknown";
  const statusColor = DECLARATION_STATUS_COLORS[dmsStatus] || "default";
  const statusLabel = DECLARATION_STATUS_LABELS[dmsStatus] || dmsStatus;

  const utbTtwAmounts = useMemo(
    () => extractUtbTtwAmountDisplays(declaration, invoices),
    [declaration, invoices]
  );

  const orderReference = order
    ? pickString(order.reference, order.customer_reference, order.booking_number, order.id)
    : undefined;

  const orderDetails = order ? formatLeanCustomsOrderDetailLine(order) : null;

  if (!visible && !loading) {
    return null;
  }

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Space wrap>
          <SafetyCertificateOutlined />
          <span>{t("bookings.leanCustoms.title")}</span>
          <img
            src="/lean_customs_logo.jpeg"
            alt="LeanCustoms"
            style={{ height: 18, width: "auto" }}
          />
        </Space>
      }
    >
      {loading ? (
        <Spin />
      ) : loadError ? (
        <Typography.Text type="danger">{loadError}</Typography.Text>
      ) : (
        <>
          {secondaryWarning ? (
            <Typography.Paragraph type="warning" style={{ marginBottom: 12 }}>
              {secondaryWarning}
            </Typography.Paragraph>
          ) : null}

          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            {orderReference ? (
              <Descriptions.Item label={t("bookings.leanCustoms.orderReference")}>
                {orderReference}
              </Descriptions.Item>
            ) : null}
            {orderDetails ? (
              <Descriptions.Item label={t("bookings.leanCustoms.orderDetails")}>
                {orderDetails}
              </Descriptions.Item>
            ) : null}
            {declaration?.reference ? (
              <Descriptions.Item label={t("customs.reference")}>
                {declaration.reference}
              </Descriptions.Item>
            ) : null}
            {declaration ? (
              <Descriptions.Item label={t("customs.declarationStatus")}>
                <Tag color={statusColor}>{statusLabel}</Tag>
              </Descriptions.Item>
            ) : null}
            {declaration?.mrn ? (
              <Descriptions.Item label={t("customs.mrn")}>
                {declaration.mrn}
              </Descriptions.Item>
            ) : null}
            {declaration?.lrn ? (
              <Descriptions.Item label={t("customs.lrn")}>
                {declaration.lrn}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {(utbTtwAmounts.utbPayment || utbTtwAmounts.ttwSurety) && (
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
              style={{ marginTop: 16 }}
              title={t("bookings.leanCustoms.amountsFromLeanCustoms")}
            >
              {utbTtwAmounts.utbPayment ? (
                <Descriptions.Item
                  label={t("bookings.leanCustoms.utbPaymentLabel")}
                >
                  {utbTtwAmounts.utbPayment}
                </Descriptions.Item>
              ) : null}
              {utbTtwAmounts.ttwSurety ? (
                <Descriptions.Item
                  label={t("bookings.leanCustoms.ttwSuretyLabel")}
                >
                  {utbTtwAmounts.ttwSurety}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          )}

          {resolvedDeclarationId ? (
            <>
              <Typography.Title level={5} style={{ marginTop: 20, marginBottom: 8 }}>
                {t("bookings.leanCustoms.documentsTitle")}
              </Typography.Title>

              {documents.length === 0 ? (
                <Empty
                  description={t("customs.noDocuments")}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Table<LeanCustomsDeclarationDocument>
                  dataSource={documents}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: t("bookings.leanCustoms.documentKindColumn"),
                      key: "document_kind",
                      width: 120,
                      render: (_: unknown, record) => {
                        const kind = getDeclarationDocumentOfficialKind(record);
                        if (kind === "utb") {
                          return <Tag color="blue">{t("bookings.leanCustoms.utbTag")}</Tag>;
                        }
                        if (kind === "ttw") {
                          return <Tag color="blue">{t("bookings.leanCustoms.ttwTag")}</Tag>;
                        }
                        return (
                          <span style={{ color: "rgba(0,0,0,0.45)" }}>
                            {record.document_type || "—"}
                          </span>
                        );
                      },
                    },
                    {
                      title: t("customs.filename"),
                      dataIndex: "filename",
                      key: "filename",
                      ellipsis: true,
                    },
                    {
                      title: t("customs.fileSize"),
                      dataIndex: "byte_size",
                      key: "byte_size",
                      width: 100,
                      render: (size: number) => formatByteSize(size ?? 0),
                    },
                    {
                      title: t("common.actions"),
                      key: "actions",
                      width: 120,
                      render: (_: unknown, record) => (
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={downloadingId === record.id}
                          onClick={() =>
                            handleDownload(record.id, record.filename || "document")
                          }
                        >
                          {t("customs.download")}
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </>
          ) : null}

          {showPortalLink && resolvedDeclarationId ? (
            <div style={{ marginTop: 16 }}>
              <Button
                type="link"
                onClick={() =>
                  navigate(
                    `/customs?declaration=${encodeURIComponent(resolvedDeclarationId)}`
                  )
                }
              >
                {t("bookings.leanCustoms.portalDeclarations")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
};
